import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Moment } from 'moment';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import DocumentTitle from '../../utilComponents/DocumentTitle';
import Paper from '../../apiForm/panel/Paper';
import { useAlert } from '../../apiForm/toast';
import useExportApi from './useExportApi';
import StatisticsIcon from '../../icons/monitoring.svg?react';

export default function TransactionsStatisticsView() {
  const { t } = useTranslation();
  const api = useExportApi();
  const alert = useAlert();
  const [from, setFrom] = useState<Moment | null>(null);
  const [to, setTo] = useState<Moment | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!from || !to || !from.isBefore(to)) {
      alert.error({
        message: t('export.transactionsStatistics.invalidDateRange'),
      });
      return;
    }

    setLoading(true);
    try {
      await api.syncTransactionsStatistics({
        from: from.toISOString(),
        to: to.toISOString(),
      });
      alert.success({
        message: t('export.transactionsStatistics.successMessage'),
      });
    } catch (e) {
      alert.error({ message: t('export.transactionsStatistics.genericError') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DocumentTitle translationID="export.transactionsStatistics.headline" />
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Paper
          legend={
            <Stack direction="row" gap={1} alignItems="center">
              <StatisticsIcon style={{ fill: 'white' }} />
              <Typography variant="body2">
                {t('export.transactionsStatistics.headline')}
              </Typography>
            </Stack>
          }
        >
          <Stack gap={3} component="form" onSubmit={handleSubmit}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              gap={2}
              sx={{ '& > *': { flex: 1 } }}
            >
              <DateTimePicker
                label={t('export.transactionsStatistics.fromLabel')}
                value={from}
                onChange={setFrom}
                disabled={loading}
                slotProps={{ textField: { required: true, fullWidth: true } }}
              />
              <DateTimePicker
                label={t('export.transactionsStatistics.toLabel')}
                value={to}
                onChange={setTo}
                disabled={loading}
                slotProps={{ textField: { required: true, fullWidth: true } }}
              />
            </Stack>
            <Alert severity="warning">
              <Typography variant="body2">
                {t('export.transactionsStatistics.syncWarning')}
              </Typography>
            </Alert>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="flex-end"
            >
              <Button
                type="submit"
                variant="contained"
                loading={loading}
                loadingPosition="start"
                fullWidth
                sx={{ minWidth: { sm: 180 }, width: { sm: 'auto' } }}
              >
                {t('export.transactionsStatistics.syncButton')}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </LocalizationProvider>
    </>
  );
}
